// Whole-page translation driven by the language dropdown in the header.
// Uses Google's website translation engine, so static text, API-driven
// product/category content and every route are translated, not just the header.

export const LANGUAGES = [
  { code: "en", label: "🇺🇸 English" },
  { code: "hi", label: "🇮🇳 हिन्दी" },
  { code: "gu", label: "🇮🇳 ગુજરાતી" },
  { code: "mr", label: "🇮🇳 मराठी" },
  { code: "ta", label: "🇮🇳 தமிழ்" },
  { code: "te", label: "🇮🇳 తెలుగు" },
  { code: "bn", label: "🇮🇳 বাংলা" },
];

const SOURCE_LANG = "en";
const CONTAINER_ID = "google_translate_element";
const SCRIPT_ID = "google-translate-script";

const setCookie = (value) => {
  const host = window.location.hostname;
  document.cookie = `googtrans=${value}; path=/`;
  // Google also reads the cookie from the bare domain when one exists.
  if (host && host !== "localhost") {
    document.cookie = `googtrans=${value}; path=/; domain=${host}`;
  }
};

const clearCookie = () => {
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const host = window.location.hostname;
  document.cookie = `googtrans=; path=/; ${expired}`;
  if (host && host !== "localhost") {
    document.cookie = `googtrans=; path=/; domain=${host}; ${expired}`;
  }
};

// Google Translate wraps text nodes in <font> tags, which makes React throw
// "removeChild / insertBefore ... not a child of this node" when it later
// updates that text. Make those two calls tolerant.
const patchDomForReact = () => {
  if (window.__translatePatched) return;
  window.__translatePatched = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
};

const ensureContainer = () => {
  let el = document.getElementById(CONTAINER_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = CONTAINER_ID;
    el.style.display = "none";
    document.body.appendChild(el);
  }
  return el;
};

const findCombo = () => document.querySelector("select.goog-te-combo");

// Load the engine once. Resolves when the hidden language <select> exists.
let loadPromise = null;
const loadEngine = () => {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    ensureContainer();

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: SOURCE_LANG,
          includedLanguages: LANGUAGES.filter((l) => l.code !== SOURCE_LANG)
            .map((l) => l.code)
            .join(","),
          autoDisplay: false,
        },
        CONTAINER_ID
      );

      // The combo is injected asynchronously after init.
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (findCombo() || tries > 50) {
          clearInterval(timer);
          resolve(findCombo());
        }
      }, 100);
    };

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    script.onerror = () => resolve(null);
    document.body.appendChild(script);
  });

  return loadPromise;
};

const applyToCombo = (combo, lang) => {
  combo.value = lang;
  combo.dispatchEvent(new Event("change"));
};

export const setPageLanguage = async (lang) => {
  document.documentElement.lang = lang;

  if (lang === SOURCE_LANG) {
    // Restoring the original text reliably needs a fresh page without the cookie.
    const wasTranslated = /(?:^|;\s*)googtrans=\/[^;]*\/(?!en\b)/.test(
      document.cookie
    );
    clearCookie();
    if (wasTranslated) window.location.reload();
    return;
  }

  setCookie(`/${SOURCE_LANG}/${lang}`);
  patchDomForReact();

  const combo = findCombo() || (await loadEngine());
  if (combo) {
    applyToCombo(combo, lang);
  }
};

// Call once on app start: restores the saved language after a refresh.
export const initPageTranslate = () => {
  let saved = "en";
  try {
    saved = localStorage.getItem("language") || "en";
  } catch (e) {
    /* storage unavailable */
  }

  document.documentElement.lang = saved;

  if (saved === SOURCE_LANG) {
    clearCookie();
    return;
  }

  setCookie(`/${SOURCE_LANG}/${saved}`);
  patchDomForReact();
  loadEngine();
};
